import { useMemo, useState } from "react";
import { useSEO, BASE } from "@/lib/seo";
import { Search } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { LEXIQUE_TERMS } from "@/lib/lexique";

const CATEGORIES = ["Tous", "Courants", "Techniques", "Matériaux", "Formes", "Édition & droits"];

const TERMS = LEXIQUE_TERMS;

const LETTERS = [...new Set(TERMS.map((t) => t.term.normalize("NFD")[0].toUpperCase()))].sort((a, b) => a.localeCompare(b, "fr"));

export default function LexiquePage() {
  useSEO(
    "Glossaire & Lexique de la Sculpture Contemporaine — Termes & Définitions",
    "Glossaire pédagogique exhaustif des termes de sculpture : ciselure, patine, épreuve d'artiste, cire perdue, assemblage, taille directe et bas-relief.",
    {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Lexique de la sculpture contemporaine",
      inLanguage: "fr-FR",
      url: `${BASE}/lexique-sculpture`,
      hasDefinedTerm: TERMS.map((t) => ({
        "@type": "DefinedTerm",
        name: t.term,
        description: t.def,
      })),
    }
  );

  const [query, setQuery] = useState("");
  const [letter, setLetter] = useState(null);
  const [cat, setCat] = useState("Tous");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TERMS.filter((t) => {
      if (q && !`${t.term} ${t.def}`.toLowerCase().includes(q)) return false;
      if (letter && t.term.normalize("NFD")[0].toUpperCase() !== letter) return false;
      if (cat !== "Tous" && t.cat !== cat) return false;
      return true;
    }).sort((a, b) => a.term.localeCompare(b.term, "fr"));
  }, [query, letter, cat]);

  return (
    <main data-testid="lexique-page" className="bg-paper pb-24 pt-28 sm:pt-36">
      <header className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal y={16}>
          <p className="eyebrow">Lexique — de A à T</p>
        </Reveal>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Les mots de la sculpture, expliqués simplement
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-smoke">
            Patine, cire perdue, épreuve d'artiste, ronde-bosse… Les termes du
            métier, définis en langage clair pour lire une œuvre — ou une fiche
            de galerie — sans dictionnaire.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto mt-14 max-w-7xl px-6 lg:px-10" data-testid="lexique-filters">
        <div className="flex items-center gap-3 border border-ink/15 bg-cream px-5 py-4">
          <Search className="h-4 w-4 shrink-0 text-bronze" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un terme…"
            data-testid="lexique-search-input"
            aria-label="Rechercher un terme du lexique"
            className="w-full bg-transparent font-serif text-xl text-ink outline-none placeholder:text-smoke/50"
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-1.5" aria-label="Filtrer par lettre">
          <button
            data-testid="lexique-letter-all"
            onClick={() => setLetter(null)}
            className={`px-2.5 py-1.5 font-mono text-[11px] uppercase transition-colors ${!letter ? "bg-ink text-paper" : "text-smoke hover:text-bronze"}`}
          >
            Tout
          </button>
          {LETTERS.map((l) => (
            <button
              key={l}
              data-testid={`lexique-letter-${l.toLowerCase()}`}
              onClick={() => setLetter(letter === l ? null : l)}
              className={`px-2.5 py-1.5 font-mono text-[11px] uppercase transition-colors ${letter === l ? "bg-bronze text-paper" : "text-smoke hover:text-bronze"}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2" aria-label="Filtrer par catégorie">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`lexique-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                cat === c ? "border-bronze bg-bronze text-paper" : "border-ink/20 text-ink/70 hover:border-bronze hover:text-bronze"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-10 max-w-7xl px-6 lg:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-smoke" data-testid="lexique-count">
          {filtered.length} terme{filtered.length > 1 ? "s" : ""}
        </p>
        {filtered.length === 0 ? (
          <p className="mt-8 font-serif text-2xl text-smoke" data-testid="lexique-empty">
            Aucun terme ne correspond à cette recherche.
          </p>
        ) : (
          <div className="mt-6 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-3" data-testid="lexique-grid">
            {filtered.map((t) => (
              <Link
                key={t.term}
                to={`/lexique-sculpture/${t.slug}`}
                data-testid={`lexique-term-${t.slug}`}
                className="group block h-full bg-paper p-7 transition-colors duration-300 hover:bg-cream"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h2 className="font-serif text-2xl text-ink transition-colors group-hover:text-bronze">{t.term}</h2>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-bronze">{t.cat}</span>
                </div>
                <p className="mt-3 text-sm font-light leading-relaxed text-smoke">{t.def}</p>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-bronze">
                  <span className="link-underline">Lire la fiche</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 border border-ink/15 bg-cream p-8">
          <p className="eyebrow">Approfondir</p>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
            <Link to="/materiaux-et-techniques" data-testid="lexique-link-materiaux" className="group inline-flex items-center gap-2 font-serif text-xl text-ink hover:text-bronze">
              <ArrowRight className="h-4 w-4 text-bronze transition-transform group-hover:translate-x-1" />
              <span className="link-underline">Matériaux et techniques</span>
            </Link>
            <Link to="/guide-achat-direct-artiste" data-testid="lexique-link-guide" className="group inline-flex items-center gap-2 font-serif text-xl text-ink hover:text-bronze">
              <ArrowRight className="h-4 w-4 text-bronze transition-transform group-hover:translate-x-1" />
              <span className="link-underline">Le guide d'achat en direct</span>
            </Link>
            <Link to="/faq" data-testid="lexique-link-faq" className="group inline-flex items-center gap-2 font-serif text-xl text-ink hover:text-bronze">
              <ArrowRight className="h-4 w-4 text-bronze transition-transform group-hover:translate-x-1" />
              <span className="link-underline">Questions fréquentes</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
